import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { emitMessage } from 'engine/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { EventEditForm } from 'forms/EventForm';
import type { EventRecord, EventStatus } from 'shared/modelTypes/event';
import { EventSchema, EventStatusSchema, type Event } from 'shared/modelTypes/event';
import { applyGameOverrides, GameVariablesOverrideSchema } from 'shared/modelTypes/variant';
import type { GameVariable } from 'shared/modelTypes/game';

export const Route = createFileRoute('/dashboard/events/edit/$id')({
	validateSearch: (search) => {
		return {
			status: EventStatusSchema.safeParse(search.status).success ? search.status : undefined,
		};
	},
	component: RouteComponent,
	loader: async ({ params }) => {
		const eventResponse = await emitMessage({ kind: 'event-find', id: params.id });

		const variantId = eventResponse.event?.variantId;
		const variantRes = variantId ? await emitMessage({ kind: 'variant-find', id: variantId }) : undefined;
		const gameId = eventResponse.event?.gameId;
		const gameRes = gameId ? await emitMessage({ kind: 'game-find', id: gameId }) : undefined;

		const gameOverrides = variantRes?.variant?.variant.gameOverrides;
		const game = gameRes?.game?.game;
		const appliedGame = game && gameOverrides ? applyGameOverrides(game, gameOverrides) : game;

		const gameVariables = appliedGame?.variables;

		if (eventResponse?.event) {
			eventResponse.event.gameOverrides ??= { variables: [] };

			const eventGameOverrides = eventResponse.event.gameOverrides;

			if (gameVariables) {
				const eventVariables = eventGameOverrides.variables;
				eventGameOverrides.variables = gameVariables.map((gameVariable) => {
					const eventVariable = eventVariables.find((v) => v.key === gameVariable.key);
					if (eventVariable) {
						return eventVariable;
					} else {
						// create a new variable override
						return GameVariablesOverrideSchema.parse({ key: gameVariable.key });
					}
				});
			}
		}

		return {
			eventResponse: eventResponse,
			directory: (await emitMessage({ kind: 'media-list-directory', folderId: null })).media,
			baseGameVariables: appliedGame?.variables,
		};
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const { status } = Route.useSearch();

	return (
		<div>
			{!data.eventResponse.event && <span>Event not found</span>}
			{data.eventResponse.event && (
				<EventUpdateForm
					event={data.eventResponse.event}
					baseGameVariables={data.baseGameVariables}
					status={status ? EventStatusSchema.parse(status) : undefined}
				/>
			)}
		</div>
	);
}

type EventUpdateFormProps = {
	event: EventRecord;
	baseGameVariables?: GameVariable[];
	status?: EventStatus;
};

function EventUpdateForm({ event, baseGameVariables, status }: EventUpdateFormProps) {
	const methods = useForm<Event>({
		// @ts-expect-error zodResolver type inference seems to be bad
		resolver: zodResolver(EventSchema),
		defaultValues: event,
	});

	const err = methods.formState.errors;

	const router = useRouter();
	const [res, setRes] = useState<Promise<unknown> | undefined>();

	const onSubmit = async (data: Event) => {
		const r = emitMessage({ kind: 'event-update', event: { ...data, id: event.id } });
		setRes(r);
		const response = await r;

		if (response.success) {
			router.invalidate();
			if (status) {
				const res = await emitMessage({ kind: 'event-set-status', id: event.id, status });
				if (res.success) {
					router.navigate({
						to: '/dashboard/events/view/$id',
						params: { id: event.id },
					});
				} else {
					toast('server did not set status', { icon: <X /> });
				}
			} else {
				router.navigate({
					to: '/dashboard/events/view/$id',
					params: { id: event.id },
				});
			}
		} else {
			toast('server did not save event', { icon: <X /> });
		}
	};

	console.log('errors', err);
	return (
		<FormProvider {...methods}>
			<form
				style={{ opacity: res ? '0.4' : '1' }}
				className="flex flex-col gap-3"
				// @ts-expect-error zodResolver type inference seems to be bad
				onSubmit={methods.handleSubmit(onSubmit)}
			>
				<EventEditForm label="Edit Event" baseGameVariables={baseGameVariables} status={status} />
				<Button type="submit">Submit</Button>
			</form>
		</FormProvider>
	);
}
